import type { ReactNode } from 'react';
import type { ApiError } from '../api/client';
import { IconAlert, IconCloudOff, IconSearch } from './Icons';

/** Card-shaped placeholder shown while a results page loads. */
export function ListingCardSkeleton() {
  return (
    <div className="sf-listing-card" aria-hidden="true">
      <div className="sf-skeleton" style={{ aspectRatio: '4 / 3' }} />
      <div className="sf-listing-card__body">
        <div className="sf-skeleton" style={{ height: 18, width: '85%' }} />
        <div className="sf-skeleton" style={{ height: 13, width: '55%' }} />
        <div className="sf-skeleton" style={{ height: 22, width: '45%', marginTop: 6 }} />
      </div>
    </div>
  );
}

export function ListingGridSkeleton({ count = 6 }: { count?: number }) {
  return (
    <div className="row g-3" role="status" aria-label="Loading listings">
      {Array.from({ length: count }, (_, index) => (
        <div className="col-12 col-sm-6 col-lg-4" key={index}>
          <ListingCardSkeleton />
        </div>
      ))}
      <span className="sf-visually-hidden">Loading listings…</span>
    </div>
  );
}

export function TableSkeleton({ rows = 5, columns = 4 }: { rows?: number; columns?: number }) {
  return (
    <div className="sf-table-wrap" role="status" aria-label="Loading">
      <table className="sf-table">
        <tbody>
          {Array.from({ length: rows }, (_, r) => (
            <tr key={r}>
              {Array.from({ length: columns }, (_, c) => (
                <td key={c}>
                  <div className="sf-skeleton" style={{ height: 14 }} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <span className="sf-visually-hidden">Loading…</span>
    </div>
  );
}

export function DetailSkeleton() {
  return (
    <div role="status" aria-label="Loading listing">
      <div className="sf-skeleton mb-3" style={{ height: 32, width: '60%' }} />
      <div className="sf-skeleton mb-4" style={{ aspectRatio: '16 / 10', borderRadius: 10 }} />
      <div className="sf-skeleton mb-2" style={{ height: 14 }} />
      <div className="sf-skeleton mb-2" style={{ height: 14, width: '92%' }} />
      <div className="sf-skeleton" style={{ height: 14, width: '70%' }} />
      <span className="sf-visually-hidden">Loading listing…</span>
    </div>
  );
}

export function EmptyState({
  icon,
  title,
  description,
  action,
}: {
  /** A node rather than a string: these are SVG icons, not emoji. */
  icon?: ReactNode;
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="sf-empty sf-card">
      <div className="sf-empty__icon" aria-hidden="true">
        {icon ?? <IconSearch />}
      </div>
      <h3 className="h5 mb-2">{title}</h3>
      {description && <p className="mb-3">{description}</p>}
      {action}
    </div>
  );
}

/**
 * Shown when a request fails. Always offers a retry: a transient network problem should
 * not leave the page in a dead end.
 */
/**
 * Shown when a request fails.
 *
 * Accepts the whole `ApiError` so it can tell the cases apart. A request that never
 * reached the server gets a retry button; a 403 does not, because trying again cannot
 * change the answer and offering the button only invites a pointless loop. Passing a bare
 * string still works for callers that have nothing richer to hand.
 *
 * None of these is an empty state: a failed request must never be rendered as "no results".
 */
export function ErrorState({
  title,
  message,
  error,
  onRetry,
}: {
  title?: string;
  message?: string;
  error?: ApiError;
  onRetry?: () => void;
}) {
  const kind = error?.kind ?? 'unknown';
  const text = message ?? error?.message ?? 'Something went wrong. Please try again.';

  const heading =
    title ??
    {
      network: 'Unable to connect',
      timeout: 'That took too long',
      auth: 'Please sign in again',
      forbidden: 'You do not have access',
      notFound: 'Not found',
      conflict: 'That could not be applied',
      server: 'RoomKart had a problem',
      validation: 'Check the details',
      unknown: 'Something went wrong',
    }[kind];

  // Retrying a 403 or a 404 cannot change the outcome, so the button is only offered
  // where it might. When no error object was passed we keep the old behaviour and trust
  // the caller's decision to supply onRetry.
  const canRetry = onRetry && (error === undefined || error.retryable);

  return (
    <div className="sf-empty sf-card" role="alert">
      <div className="sf-empty__icon" aria-hidden="true">
        {kind === 'network' || kind === 'timeout' ? <IconCloudOff /> : <IconAlert />}
      </div>
      <h3 className="h5 mb-2">{heading}</h3>
      <p className="mb-3">{text}</p>
      {canRetry && (
        <button type="button" className="btn btn-primary" onClick={onRetry}>
          Try again
        </button>
      )}
    </div>
  );
}

/**
 * A one-line failure above a form or list.
 *
 * Takes a string or an `ApiError`, because some callers hold one and some the other, and
 * making every call site unwrap it was noisier than accepting both here.
 */

/**
 * A one-line failure above a form or list.
 *
 * Takes a string or an `ApiError`, because some callers hold one and some the other, and
 * making every call site unwrap it was noisier than accepting both here.
 */
export function InlineError({ message }: { message: string | ApiError | null | undefined }) {
  if (!message) return null;

  const text = typeof message === 'string' ? message : message.message;

  return (
    <div className="alert alert-danger py-2 px-3 mb-3" role="alert">
      {text}
    </div>
  );
}

export function InlineSuccess({ message }: { message: string | null | undefined }) {
  if (!message) return null;

  return (
    <div className="alert alert-success py-2 px-3 mb-3" role="status">
      {message}
    </div>
  );
}

export function Spinner({ label = 'Loading' }: { label?: string }) {
  return (
    <div className="d-flex align-items-center gap-2 text-muted" role="status">
      <span className="spinner-border spinner-border-sm" aria-hidden="true" />
      <span>{label}…</span>
    </div>
  );
}

/**
 * Labels anything produced by the development payment gateway. Simulated money must never
 * be mistaken for a real collection, on any screen.
 */
export function SimulatedPaymentNotice({ children }: { children?: ReactNode }) {
  return (
    <div className="sf-simulated-banner" role="note">
      <strong>Development mode.</strong>{' '}
      {children ?? 'No money is taken and nothing is charged. These payments are recorded as simulated.'}
    </div>
  );
}
